import {
  arrow,
  autoPlacement,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  hide,
  inline,
  offset,
  useFloating,
  useFocus,
  useHover,
  useInteractions
} from '@floating-ui/react';
import clsx from 'clsx';
import {
  cloneElement,
  CSSProperties,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useRef,
  useState
} from 'react';
import { useThemeColorPalette } from '../../hooks/use-theme-color-palette';
import '../../styles/vendor/_tippy.scss';
import { ThemeColors } from '../../theme';
import classes from './tooltip.module.scss';

export type TooltipProps = PropsWithChildren & {
  /**
   * The content of the tooltip
   */
  content: ReactNode;

  /**
   * The children of the tooltip
   */
  children: ReactElement;

  /**
   * Whether the tooltip is active
   */
  active?: boolean;

  /**
   * The class name of the tooltip
   */
  className?: string;

  /**
   * The `color` property specifies the color theme to be applied.
   * It is an optional property and can accept any value defined in the `ThemeColors` type.
   * The value determines the visual styling and theme color of components.
   */
  color?: ThemeColors;

  /**
   * Delay for the popup to throw 
   */
  delay?: number;
};

export function Tooltip({
  children,
  content,
  active = true,
  className,
  color,
  delay,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);
  const palette = useThemeColorPalette(color ?? null);

  const { refs, floatingStyles, context } = useFloating({
    open: !!children && !!content && active && isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    middleware: [
      offset(16),
      autoPlacement({
        allowedPlacements: ['top', 'bottom'],
      }),
      inline(),
      hide({}),
      arrow({
        element: arrowRef,
      }),],
    whileElementsMounted: autoUpdate
  });

  const hover = useHover(context, { move: true, delay: delay ?? 800 });
  const focus = useFocus(context, {});
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
  ]);

  if (!children || !content || !active) {
    return children;
  }

  return (
    <>
      {isValidElement(children) ? cloneElement(children, {
        ...children.props,
        className: clsx(className, children.props?.className),
        ref: refs.setReference,
        ...getReferenceProps()
      }) : children}
      {isOpen && (
        <FloatingPortal>
          <div
            className={clsx(classes.root, 'tooltip-component')}
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              '--tooltip--background': palette?.main,
              '--tooltip--text-color': palette?.contrast
            } as CSSProperties}
            {...getFloatingProps()}
          >
            <FloatingArrow className={classes.arrow} ref={arrowRef} context={context} />
            {content}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}